import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, BellOff, ChevronLeft } from "lucide-react";
import { Button, EmptyState, Spinner, Icon, MDisplay } from "@cntrl/ui";
import { isLiveActivityMuted, liveActivitySupported } from "@cntrl/native";
import type { components } from "@cntrl/api-client";
import { useDos } from "../../app/hooks/useDayOfShow.js";
import { useTravel } from "../../app/hooks/useTravel.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { useOnlineStatus } from "../../app/hooks/useOnlineStatus.js";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { BusSheetButton } from "./BusSheetButton.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { useApp } from "../../app/appContext.js";
import { muteLiveActivity, unmuteLiveActivity } from "../../app/liveActivitySync.js";
import { pushToast } from "../../app/toasts.js";
import { DosSettlementCard } from "./DosSettlementCard.js";
import { PlaceActionsSheet } from "./PlaceActionsSheet.js";
import { RunOfShowClock } from "./RunOfShowClock.js";
import { ownSlotId } from "../../app/showLineup.js";
import { OfflineBanner } from "./OfflineBanner.js";
import { FoodFinderCard } from "./FoodFinderCard.js";
import {
  runOfShowLabel,
  runOfShowRows,
  parseTimeToMinutes,
  venueNowMinutes,
  formatClock,
  formatShowDate,
  isoDateOffset,
  showDayDelta,
  futureShowHeadline,
  listCardDate,
  type PlaceRef,
} from "./dayOfShow.js";
import styles from "./dayOfShow.module.scss";

type TravelItemDto = components["schemas"]["TravelItemDto"];
type ShowDetail = components["schemas"]["ShowDetail"];
type ScheduleItemDto = components["schemas"]["ScheduleItemDto"];

/**
 * The composed on-phone Day-of-Show for one resolved show — the flagship DARK live surface (ART-174). Section
 * order follows the spec: Essentials → Now → Run of show → Settlement. Travel / flight / weather live on the
 * Logistics page, NOT here (spec §"intentionally absent from DOS"). Joins `show:{id}` so the live
 * `settlement-updated` / `schedule-changed` signals refetch through the invalidation
 * registry; reconnect + foreground resync are handled centrally by RealtimeProvider.
 * MONEY-FREE except the {@link DosSettlementCard}, a SEPARATE money-gated endpoint absent by construction
 * for a no-money seat. Online-first: an honest offline banner + writes disabled while offline.
 */
export function DayOfShowDetail({
  showId,
  onBack,
  onOpenGuests,
}: {
  showId: string;
  onBack?: () => void;
  onOpenGuests: (showId: string) => void;
  /** Kept for the caller's shape — the VIP + Logistics tiles were cut from DOS (Ryan, ART-174). */
  onOpenVip?: (showId: string) => void;
  onOpenLogistics?: (showId: string) => void;
}) {
  useLiveGroup(`show:${showId}`);
  const online = useOnlineStatus();
  const dos = useDos(showId);
  const travel = useTravel(showId);
  const [foodOpen, setFoodOpen] = useState(false);
  // The venue/hotel tap-actions sheet (Copy · Open Address · Lyft · Uber) — one at a time (Ryan, ART-174).
  const [place, setPlace] = useState<PlaceRef | null>(null);
  const canGuests = useCapability("guests:read");
  const { bandId } = useBandContext();

  if (dos.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading day of show" />
      </div>
    );
  }
  if (dos.isError || !dos.data?.show) {
    return (
      <div className={styles.screen}>
        {!online && <OfflineBanner />}
        <EmptyState
          title="Couldn't load the day of show"
          description={dos.error instanceof ApiError ? dos.error.message : "Check your connection and try again."}
          action={<Button onClick={() => void dos.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  const v = dos.data;
  const show = v.show!;
  const city = show.city ?? "";
  const items = v.runOfShow ?? [];
  const hotel = (travel.data?.items ?? []).find((t) => t.kind === "hotel") ?? null;
  // Our own act on this bill — "Your Set" and the set-row highlight both key off it.
  // Keyed on the MOUNTED band, not the show's owner: on a shared show (ART-249) `show.bandId` is the host,
  // and our set is the guest band's slot.
  const ourSlot = ownSlotId(show.lineup, bandId);
  // A guest band cannot register a Live Activity yet: its plan/token rows key (show_id, band_id) onto the
  // HOST's show row (ART-208 owns that).
  const isGuestShow = show.bandId !== bandId;
  const hasTimeline = items.some((i) => parseTimeToMinutes(i.startsAt) != null);
  // A festival settles on a flat guarantee — the money card skips the door grid (handoff).
  const isFest = show.kind === "festival" || show.kind === "fest";
  // The run of show is wall-clock TIMES with no date, so the live/wrapped hero is only meaningful ON the show
  // day. A future show gets a day-countdown hero instead of "all wrapped" (Ryan, ART-174).
  const todayIso = isoDateOffset(0);
  const dayDelta = showDayDelta(show.date, todayIso);
  const venueCityLine = [show.venueName, show.city].filter(Boolean).join(" · ");

  return (
    <div className={styles.screen} data-testid="dos-detail">
      {!online && <OfflineBanner />}

      <header className={styles.header}>
        {onBack && (
          <button type="button" className={styles.headerBack} onClick={onBack} aria-label="Back to shows">
            <Icon icon={ChevronLeft} size={20} />
          </button>
        )}
        <div className={styles.headerText}>
          <span className={styles.headerEyebrow}>Day of Show</span>
          <MDisplay className={styles.headerTitle}>
            {[show.venueName || "Day of Show", formatShowDate(show.date)].filter(Boolean).join(" · ")}
          </MDisplay>
        </div>
        <div className={styles.headerActions}>
          <BusSheetButton />
          {!isGuestShow && (
            <LiveActivityMuteButton bandId={show.bandId!} showId={showId} items={items} disabled={!online} />
          )}
        </div>
      </header>

      {/* Desktop: the day's cards in a left column, settlement on the right. Below the breakpoint both wrappers
          are `display: contents`, so the phone keeps its single column. */}
      <div className={styles.dosColumns}>
        <div className={styles.dosMain}>
          <EssentialsGrid
            hotel={hotel}
            show={show}
            canGuests={canGuests}
            onPlace={setPlace}
            onFood={() => setFoodOpen(true)}
            onGuests={() => onOpenGuests(showId)}
          />

          {dayDelta > 0 ? (
            // A show that hasn't happened yet — count down in DAYS, never live or wrapped (Ryan, ART-174).
            <div className={styles.now} data-mode="future" data-testid="dos-future-hero">
              <div className={styles.nowHead}>
                <span className={styles.nowEyebrow}>{listCardDate(show.date, todayIso)}</span>
              </div>
              <div className={styles.nowTitle}>{futureShowHeadline(dayDelta)}</div>
              <div className={styles.nowSub}>{venueCityLine}</div>
              {!hasTimeline && (
                <p className={styles.nowHint}>Add doors + set time on desktop to activate the run of show.</p>
              )}
            </div>
          ) : hasTimeline ? (
            <RunOfShowClock items={items} timezone={show.timezone} dayDelta={dayDelta} />
          ) : (
            <div className={styles.now} data-mode="wrapped">
              <div className={styles.nowEyebrow}>Run of show</div>
              <div className={styles.nowTitle}>Not set yet</div>
              <p className={styles.nowHint}>Add doors + set time on desktop to activate the run of show.</p>
            </div>
          )}

          <RunOfShowList items={items} timezone={show.timezone} ourSlotId={ourSlot} />

          <FoodFinderCard showId={showId} city={city} open={foodOpen} onOpenChange={setFoodOpen} online={online} />
        </div>

        {place && <PlaceActionsSheet place={place} onClose={() => setPlace(null)} />}

        {/* The money-gated settlement — the DOS-specific card (NOT the desktop worksheet). A SEPARATE endpoint,
            absent by construction for a no-money seat. Auto-saves on a debounce; edits sync live to desktop
            over `show:{id}`. Splits/expenses are desktop-only; no VIP inputs (Ryan, ART-174). */}
        <div className={styles.dosAside}>
          <DosSettlementCard showId={showId} isFest={isFest} isGuestShow={isGuestShow} />
        </div>
      </div>
    </div>
  );
}

// ---- sub-components --------------------------------------------------------------------------------------

/**
 * Mute / unmute this show's lock-screen Live Activity ON THIS PHONE (ART-208). The card itself is automatic — the
 * app registers it for every show you're on, across all your bands (`app/liveActivitySync.ts`) — so the only thing
 * left to decide here is "not on my phone". iOS-ONLY; hidden when the run of show has no timed set (no card exists).
 */
function LiveActivityMuteButton({
  bandId,
  showId,
  items,
  disabled,
}: {
  bandId: string;
  showId: string;
  items: readonly ScheduleItemDto[];
  disabled?: boolean;
}) {
  const { api } = useApp();
  const supported = liveActivitySupported();
  const hasSet = items.some((i) => i.section === "stage" && parseTimeToMinutes(i.startsAt) != null);
  const [muted, setMuted] = useState<boolean | null>(null);
  // Taps are queued, not blocked: each flips the bell AT ONCE (no spinner) and its write runs after the one before
  // it, so rapid taps land in order. A failed write rolls the bell back to where that write started, with a toast.
  const queue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!supported) return;
    let live = true;
    void isLiveActivityMuted(showId).then((m) => live && setMuted(m));
    return () => {
      live = false;
    };
  }, [supported, showId]);

  if (!supported || !hasSet || muted === null) return null;

  const onClick = () => {
    const from = muted;
    const to = !from;
    setMuted(to);
    queue.current = queue.current.then(async () => {
      try {
        if (to) await muteLiveActivity(api, bandId, showId);
        else await unmuteLiveActivity(api, showId);
      } catch (err) {
        setMuted(from);
        pushToast(err instanceof Error ? err.message : "Couldn't change the Live Activity");
      }
    });
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={disabled}
      aria-pressed={muted}
      aria-label={muted ? "Unmute the lock-screen Live Activity" : "Mute the lock-screen Live Activity"}
      onClick={onClick}
    >
      <Icon icon={muted ? BellOff : Bell} size={16} />
    </Button>
  );
}

/** The DOS essentials — four COMPACT tiles (Ryan, ART-174): no section head, no leading ICONS, no VIP/Media
 *  (VIP parked; media ships days after go-live). Content is top-aligned beside a top-right arrow; the venue and
 *  hotel tiles carry an eyebrow (with the icons gone, it is what says which place a name belongs to). Hotel +
 *  venue open the tap-actions sheet (Copy · Open Address · Lyft · Uber); food + guest list keep their own
 *  destinations. */
function EssentialsGrid({
  hotel,
  show,
  canGuests,
  onPlace,
  onFood,
  onGuests,
}: {
  hotel: TravelItemDto | null;
  show: ShowDetail;
  canGuests: boolean;
  onPlace: (place: PlaceRef) => void;
  onFood: () => void;
  onGuests: () => void;
}) {
  // A standard comma-separated address (what you'd type by hand) — for Copy, maps and the Uber geocode. The
  // venue carries no street, so it is city/state/country; the hotel keeps its own address line.
  const venueAddress = [show.city, show.state, show.country].filter(Boolean).join(", ");
  const hotelPlace: PlaceRef | null = hotel
    ? {
        name: hotel.propertyName,
        address: hotel.address ?? venueAddress,
        lat: hotel.lat,
        lng: hotel.lng,
        confirmationCode: hotel.confirmationCode,
      }
    : null;
  const venuePlace: PlaceRef | null = show.venueName || show.city
    ? { name: show.venueName, address: venueAddress, lat: show.lat, lng: show.lng }
    : null;
  return (
    <div className={styles.essentials} data-testid="dos-essentials">
      <EssentialTile eyebrow="Hotel" title={hotel?.propertyName ?? "No hotel yet"}
        onClick={() => hotelPlace && onPlace(hotelPlace)} disabled={!hotelPlace} />
      <EssentialTile eyebrow="Venue" title={show.venueName ?? "Venue"}
        onClick={() => venuePlace && onPlace(venuePlace)} disabled={!venuePlace} />
      <EssentialTile title="Find food"
        onClick={onFood} disabled={!show.city} />
      {canGuests && (
        <EssentialTile title="Guest list" onClick={onGuests} />
      )}
    </div>
  );
}

function EssentialTile({
  eyebrow,
  title,
  onClick,
  disabled,
}: {
  eyebrow?: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className={styles.essentialTile} onClick={onClick} disabled={disabled}>
      <span className={styles.essentialBody}>
        {eyebrow && <span className={styles.essentialEyebrow}>{eyebrow}</span>}
        <span className={styles.essentialTitle}>{title}</span>
      </span>
      {/* The arrow IS the tappability affordance — only on an ENABLED tile; a disabled one has none.
          Right, vertically centred with the text (Ryan, ART-174). */}
      {!disabled && (
        <svg className={styles.essentialArrow} width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}

/** Re-render cadence for the run-of-show row states — a pure local 30s tick (matches the Now card). */
const ROW_TICK_MS = 30_000;

export function RunOfShowList({
  items,
  timezone,
  ourSlotId,
  mineTag = "YOU",
}: {
  items: readonly ScheduleItemDto[];
  timezone: string | null | undefined;
  /** Highlights OUR set row + tags it YOU. Without it the marker landed on whichever `stage` row came first. */
  ourSlotId: string | null;
  /** The tag on that row — "YOU" for the band; the partner view (ART-239) tags it with THE MEMBER's first name. */
  mineTag?: string;
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), ROW_TICK_MS);
    return () => clearInterval(id);
  }, []);
  const nowMin = venueNowMinutes(timezone);
  const rows = useMemo(() => runOfShowRows(items, nowMin, ourSlotId), [items, nowMin, ourSlotId]);

  return (
    <div className={styles.card} data-testid="dos-run-of-show">
      <span className={styles.sectionEyebrow}>Run of show</span>
      {rows.length === 0 ? (
        <p className={styles.empty}>No run of show yet.</p>
      ) : (
        <div className={styles.runlist}>
          {rows.map((r, idx) => (
            <div
              key={r.item.itemId ?? idx}
              className={styles.runrow}
              data-status={r.status}
              data-mine={r.mine || undefined}
            >
              <span className={styles.runTime}>{formatClock(r.item.startsAt) || "—"}</span>
              <span className={styles.runWho}>
                <span className={styles.runWhoText}>{runOfShowLabel(r.item)}</span>
                {r.isLoadIn && <span className={styles.runLoadTag}>LOAD IN</span>}
                {r.isSoundcheck && <span className={styles.runScTag}>SOUNDCHECK</span>}
                {r.mine && <span className={styles.runYouTag}>{mineTag}</span>}
              </span>
              {r.status === "live" ? (
                <span className={styles.runLivePill}>LIVE</span>
              ) : r.status === "past" ? (
                <span className={styles.runDoneTag}>DONE</span>
              ) : (
                <span className={styles.runDuration}>{r.durationLabel}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
